import type { Geofence } from './types';

export interface LocationMeasurement {
  latitude?: number;
  longitude?: number;
  accuracy_m?: number;
  location_error?: string;
}

export function missingLocationReason(pos: LocationMeasurement): string | null {
  if (!Number.isFinite(pos.latitude) || !Number.isFinite(pos.longitude) || Math.abs(pos.latitude!) > 90 || Math.abs(pos.longitude!) > 180) {
    return ({ permission_denied: 'Standortfreigabe verweigert', timeout: 'Zeitüberschreitung bei der Standortermittlung', unsupported: 'Standortermittlung nicht unterstützt' } as Record<string, string>)[pos.location_error || ''] || 'Standort technisch nicht verfügbar';
  }
  if (!Number.isFinite(pos.accuracy_m) || pos.accuracy_m! < 0 || pos.accuracy_m! > 100) return 'Messung nicht genau genug (maximal 100 m)';
  return null;
}

export async function collectLocation(getPosition: () => Promise<GeolocationPosition>): Promise<LocationMeasurement> {
  try {
    const { coords } = await getPosition();
    return { latitude: coords.latitude, longitude: coords.longitude, accuracy_m: coords.accuracy };
  } catch (error) {
    const code = (error as { code?: number })?.code;
    return { location_error: code === 1 ? 'permission_denied' : code === 3 ? 'timeout' : code === 0 ? 'unsupported' : 'unavailable' };
  }
}

export function locationDecision(pos: LocationMeasurement, fences: Geofence[]) {
  const missing = missingLocationReason(pos);
  if (missing) return { allowed: true, reason: `Standort nicht erfasst: ${missing}` };
  const rad = Math.PI / 180;
  const match = fences.filter(f => f.active).find(f => {
    const a = Math.sin((f.latitude - pos.latitude!) * rad / 2) ** 2 + Math.cos(pos.latitude! * rad) * Math.cos(f.latitude * rad) * Math.sin((f.longitude - pos.longitude!) * rad / 2) ** 2;
    return 6371000 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)) <= f.radius_m;
  });
  return { allowed: !!match, reason: match ? `innerhalb ${match.name}` : 'außerhalb aller aktiven Firmenstandorte' };
}

// App access is authenticated separately. Location is audit information, never
// an admission rule. Keep the restrictive decision above for clock-out only.
export function accessLocationDecision(pos: LocationMeasurement, fences: Geofence[]) {
  return { ...locationDecision(pos, fences), allowed: true };
}
